import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BUILD_INFO, RUNTIME_INFO, formatBuildTime, ageOfBuild } from '@/lib/build-info';
import { cn } from '@/lib/cn';
import { useThemeStore } from '@/lib/theme-store';
import { GitCommit, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Always-visible build identity pill. Clicking expands to full build info.
 * Lets you verify in <1 second which build is live.
 */
export function BuildInfoBadge() {
  const [expanded, setExpanded] = useState(false);
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);

  const isProd = BUILD_INFO.buildEnv === 'production';
  const envColor =
    BUILD_INFO.buildEnv === 'production' ? 'bg-green-900/70 text-green-300 border-green-700' :
    BUILD_INFO.buildEnv === 'preview'    ? 'bg-amber-900/70 text-amber-300 border-amber-700' :
    BUILD_INFO.buildEnv === 'staging'    ? 'bg-blue-900/70 text-blue-300 border-blue-700' :
                                           'bg-slate-800/70 text-slate-400 border-slate-600';

  return (
    <>
      {/* Collapsed pill — v1: rounded-full pastel chip. v2: engraved
          serial-number plate (sharp corners, brushed-metal face,
          mono-cap label, top highlight + bottom shadow rim). The
          cockpit metaphor: this is the laser-etched serial plate
          riveted into the lower right corner of the apparatus
          operator panel, identifying the firmware running. */}
      <button
        onClick={() => setExpanded(true)}
        className={cn(
          'fixed bottom-3 right-3 z-30 flex items-center gap-1.5 px-3 py-1.5 font-mono shadow-lg backdrop-blur transition-opacity hover:opacity-100',
          isProd ? 'opacity-60' : 'opacity-90',
          cockpitV2
            ? cn(
                // Engraved serial-number plate
                'text-[10px] tracking-widest uppercase rounded-sm border',
                'bg-gradient-to-b from-[#5a626d] to-[#2e3439] border-[var(--cockpit-edge-deep)] text-[var(--engraved-ink-active)]',
              )
            : cn(
                // v1 — rounded-full pastel chip
                'text-xs rounded-full border',
                envColor,
              ),
        )}
        style={cockpitV2 ? {
          // Engraved-plate physics: top highlight + bottom shadow
          // rim, drop shadow underneath. Mirrors the .metal-bevel
          // class but inline so it doesn't fight the existing v1
          // class from theme-console.css.
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.40), 0 1px 2px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.30)',
          textShadow: '0 -1px 0 rgba(0,0,0,0.55)',
        } : undefined}
        title="Click for build details"
      >
        <GitCommit size={12} className={cockpitV2 ? 'text-[var(--cockpit-text-on-panel-3)]' : undefined} />
        <span>{BUILD_INFO.commit}</span>
        <span className="opacity-60">·</span>
        <span className="uppercase">{BUILD_INFO.buildEnv}</span>
        {BUILD_INFO.dirty && (
          <>
            <span className="opacity-60">·</span>
            <span className={cockpitV2 ? 'text-amber-300' : 'text-red-400'} title="Built with uncommitted changes">DIRTY</span>
          </>
        )}
      </button>

      {/* Expanded modal */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-3 sm:items-center sm:justify-center bg-black/60" onClick={() => setExpanded(false)}>
          <div
            className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <Info size={18} className="text-brand-400" />
                <span className="font-semibold text-slate-200">Build Info</span>
              </div>
              <button onClick={() => setExpanded(false)} className="rounded p-1 hover:bg-slate-800">
                <X size={18} />
              </button>
            </div>

            <dl className="divide-y divide-slate-800 font-mono text-sm">
              <Row label="Commit">
                <span className="text-slate-200">{BUILD_INFO.commit}</span>
                <span className="text-slate-500 text-xs ml-2">({BUILD_INFO.commitFull.slice(0, 12)}…)</span>
              </Row>
              <Row label="Branch">{BUILD_INFO.branch}</Row>
              <Row label="Environment">
                <span className={cn(
                  'rounded px-1.5 py-0.5 text-xs uppercase',
                  envColor,
                )}>{BUILD_INFO.buildEnv}</span>
              </Row>
              <Row label="Built">
                <span>{formatBuildTime(BUILD_INFO.buildTime)}</span>
                <span className="text-slate-500 text-xs ml-2">({ageOfBuild()})</span>
              </Row>
              <Row label="Node">{BUILD_INFO.nodeVersion}</Row>
              {BUILD_INFO.dirty && (
                <Row label="Status">
                  <span className="flex items-center gap-1 text-red-400">
                    <AlertTriangle size={14} /> Built with uncommitted changes
                  </span>
                </Row>
              )}
              <Row label="Supabase">
                <span className="text-slate-400 text-xs break-all">
                  {RUNTIME_INFO.supabaseUrl ?? '(not configured — dev mode)'}
                </span>
              </Row>
              <Row label="Online">
                {RUNTIME_INFO.online ? (
                  <span className="text-ems-green">yes</span>
                ) : (
                  <span className="text-ems-amber">no (offline)</span>
                )}
              </Row>
            </dl>

            <div className="border-t border-slate-700 px-4 py-3 flex gap-2">
              <Link
                to="/debug"
                onClick={() => setExpanded(false)}
                className="btn-secondary text-xs"
              >
                Full debug view
              </Link>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(JSON.stringify(BUILD_INFO, null, 2));
                }}
                className="btn-secondary text-xs"
              >
                Copy to clipboard
              </button>
              <button
                onClick={() => hardReload()}
                className="btn-secondary text-xs ml-auto"
                title="Unregister service worker, clear caches, reload"
              >
                Force refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 px-4 py-2">
      <dt className="text-slate-500 text-xs uppercase tracking-wide self-center">{label}</dt>
      <dd className="text-slate-200">{children}</dd>
    </div>
  );
}

/** Nuclear cache bust — unregisters service worker, clears caches, reloads. */
export async function hardReload() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore — best-effort */
  }
  // Bypass browser cache on reload
  window.location.reload();
}
